import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDeleteProgramFile, useMoveProgramFile, useUploadProgramFiles } from '@/api/hooks';
import { ConfirmModal } from '@/components/ConfirmModal';
import type { DirStats } from '@/components/ProgramFilesTabs';
import { DRILL_SIDES, ProgramFilesTabs } from '@/components/ProgramFilesTabs';
import { SortableFileRow } from '@/components/SortableFileRow';
import type { DrillSide, ProjectDetail } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';

export const ProgramFilesSection = ({
  project,
  projectId,
  isLocked,
  activeDir,
  onDirChange,
}: {
  project: ProjectDetail;
  projectId: string;
  isLocked: boolean;
  activeDir: DrillSide;
  onDirChange: (dir: DrillSide) => void;
}): React.ReactElement => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<{ id: string; filename: string } | null>(null);

  const uploadProgramFilesMutation = useUploadProgramFiles(projectId);
  const deleteProgramFileMutation = useDeleteProgramFile(projectId);
  const moveFileMutation = useMoveProgramFile(projectId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const filesForDir = useMemo(
    () => project.program_files.filter((f) => f.drill_side === activeDir),
    [project.program_files, activeDir]
  );
  const { t } = useLingui();

  const dirStats = useMemo((): Record<DrillSide, DirStats> => {
    return Object.fromEntries(
      DRILL_SIDES.map((dir) => {
        const dirFiles = project.program_files.filter((f) => f.drill_side === dir);
        return [
          dir,
          {
            fileCount: dirFiles.length,
            collisionCount: dirFiles.reduce((sum, f) => sum + (f.collisions?.length ?? 0), 0),
          },
        ];
      })
    ) as Record<DrillSide, DirStats>;
  }, [project.program_files]);

  const deletingProgramId = deleteProgramFileMutation.isPending ? (deleteProgramFileMutation.variables ?? null) : null;

  const handleProgramDrop = (files: File[]): void => {
    if (!files.length) return;
    uploadProgramFilesMutation.mutate(
      { files, drillSide: activeDir },
      {
        onSuccess: () => toast.success(plural(files.length, { one: '# file uploaded.', other: '# files uploaded.' })),
        onError: (e) => toast.error(apiErrorMessage(e, t`Failed to upload files.`)),
      }
    );
  };

  const openDeleteConfirm = (programId: string, filename: string): void => {
    setProgramToDelete({ id: programId, filename });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = (): void => {
    if (!programToDelete) return;
    deleteProgramFileMutation.mutate(programToDelete.id, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        setProgramToDelete(null);
        toast.success(t`File removed.`);
      },
      onError: (e) => toast.error(apiErrorMessage(e, t`Failed to remove file.`)),
    });
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over) return;
    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    if (activeIdStr === overIdStr) return;

    const activeIndex = filesForDir.findIndex((f) => f.id === activeIdStr);
    const overIndex = filesForDir.findIndex((f) => f.id === overIdStr);
    if (activeIndex === -1 || overIndex === -1) return;

    // When dragging upward the active item lands before `over`, so its predecessor
    // is the item before `over`. When dragging downward, removing the active item
    // shifts indices left, making `over` itself the effective predecessor.
    const afterId =
      activeIndex > overIndex ? (overIndex === 0 ? null : filesForDir[overIndex - 1].id) : filesForDir[overIndex].id;

    const reorderedFiles = [...project.program_files];
    const activeIdx = reorderedFiles.findIndex((f) => f.id === activeIdStr);
    const overIdx = reorderedFiles.findIndex((f) => f.id === overIdStr);
    const [moved] = reorderedFiles.splice(activeIdx, 1);
    reorderedFiles.splice(overIdx, 0, moved);

    moveFileMutation.mutate(
      { fileId: activeIdStr, afterId, optimisticFiles: reorderedFiles },
      { onError: (e) => toast.error(apiErrorMessage(e, t`Failed to reorder files.`)) }
    );
  };

  return (
    <>
      <ProgramFilesTabs
        activeDir={activeDir}
        onDirChange={onDirChange}
        dirStats={dirStats}
        onDrop={handleProgramDrop}
        isUploading={uploadProgramFilesMutation.isPending}
        disabled={isLocked}
      >
        {filesForDir.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className="w-full">
              <tbody>
                <SortableContext items={filesForDir.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  {filesForDir.map((pf) => (
                    <SortableFileRow
                      key={pf.id}
                      file={pf}
                      onDelete={openDeleteConfirm}
                      deletingProgramId={deletingProgramId}
                      disabled={isLocked}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        )}
      </ProgramFilesTabs>

      <ConfirmModal
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) setProgramToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t`Delete program file?`}
        message={t`This action cannot be undone. The program file will be permanently deleted.`}
        confirmLabel={t`Remove`}
        confirmVariant="danger"
        isLoading={deleteProgramFileMutation.isPending}
      />
    </>
  );
};
